import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { marked } from 'marked';
import { X, Clock, CheckCircle } from 'lucide-react';
import { updateScript, updateScriptApproval, exportScript, Script } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface ScriptModalProps {
    script: Script;
    initialMode: 'view' | 'edit';
    onClose: () => void;
    onSaved: (updated: Script) => void;
    articleTitle?: string;
}

const cleanScriptText = (raw: string): string => {
    let text = raw.replace(/Generate article:\s*/gi, '');
    const bodyStart = text.search(/^\*\*/m);
    if (bodyStart > 0) text = text.slice(bodyStart);
    return text.trim();
};

const toHtml = (text: string): string => {
    if (!text) return '';
    if (text.trim().startsWith('<')) return text; // already HTML from a previous edit
    return marked.parse(cleanScriptText(text)) as string;
};

// Strips production-only blocks (e.g. [ON-SCREEN TEXT], [VISUAL], [B-ROLL])
// leaving only scene headings and character dialogue.
// Handles both plain markdown (AI-generated) and HTML (edited via TipTap).
const filterForCast = (text: string): string => {
    if (!text) return text;

    if (text.trim().startsWith('<')) {
        // HTML content — use DOMParser to remove production blocks
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const body = doc.body;
        const toRemove: Element[] = [];
        let inProductionBlock = false;

        for (const el of Array.from(body.children)) {
            if (el.tagName === 'P' && el.innerHTML.includes('<strong>[')) {
                // Production block header: <p><strong>[ON-SCREEN TEXT]:</strong></p>
                toRemove.push(el);
                inProductionBlock = true;
            } else if (inProductionBlock && (el.tagName === 'UL' || el.tagName === 'OL')) {
                // List content belonging to the production block
                toRemove.push(el);
            } else {
                inProductionBlock = false;
            }
        }

        toRemove.forEach(el => el.remove());
        return body.innerHTML;
    }

    // Plain markdown — filter by double-newline blocks
    const blocks = text.split(/\n\n+/);
    return blocks.filter(block => !block.trimStart().startsWith('**[')).join('\n\n');
};

const ScriptModal: React.FC<ScriptModalProps> = ({ script, initialMode, onClose, onSaved, articleTitle }) => {
    const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
    const [castMode, setCastMode] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved'>(
        (script.approval_status as 'pending' | 'approved') || 'pending'
    );
    const [isUpdatingApproval, setIsUpdatingApproval] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const rawContent = script.script_content || '';

    const editor = useEditor({
        extensions: [StarterKit],
        content: toHtml(rawContent),
        editable: true,
        onUpdate: () => setHasUnsavedChanges(true),
    });

    useEffect(() => {
        if (editor) {
            editor.setEditable(mode === 'edit');
        }
    }, [mode, editor]);

    const handleClose = useCallback(() => {
        if (hasUnsavedChanges && mode === 'edit') {
            if (!window.confirm('You have unsaved changes. Close without saving?')) return;
        }
        onClose();
    }, [hasUnsavedChanges, mode, onClose]);

    const handleSave = async () => {
        if (!editor) return;
        setIsSaving(true);
        setSaveError('');
        try {
            const content = editor.getText() ? editor.getHTML() : '';
            const updated = await updateScript(script.script_id, content);
            setHasUnsavedChanges(false);
            onSaved(updated);
            setMode('view');
        } catch (err: any) {
            setSaveError(getApiError(err, 'Failed to save script'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleApprovalToggle = async () => {
        const next = approvalStatus === 'approved' ? 'pending' : 'approved';
        setIsUpdatingApproval(true);
        try {
            const updated = await updateScriptApproval(script.script_id, next);
            setApprovalStatus(updated.approval_status as 'pending' | 'approved');
            onSaved(updated);
        } catch (err: any) {
            // silently ignore — the badge will stay as-is
        } finally {
            setIsUpdatingApproval(false);
        }
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) handleClose();
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const blob = await exportScript(script.script_id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safeName = script.title.replace(/[^\w\s\-]/g, '').trim() || 'script';
            a.download = `${safeName}.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            // Non-intrusive: button re-enables, user can retry
        } finally {
            setIsExporting(false);
        }
    };

    const viewHtml = castMode
        ? toHtml(filterForCast(rawContent))
        : toHtml(rawContent);

    return (
        <div className="script-modal-backdrop" onClick={handleBackdropClick}>
            <div className="script-modal">
                <div className="script-modal-header">
                    <div className="script-modal-title-row">
                        <h2 className="script-modal-title">{articleTitle || script.title}</h2>
                        <div className="script-modal-mode-toggle">
                            <button
                                className={`mode-tab ${mode === 'view' ? 'active' : ''}`}
                                onClick={() => {
                                    if (mode === 'edit' && hasUnsavedChanges) {
                                        if (!window.confirm('Discard unsaved changes?')) return;
                                        setHasUnsavedChanges(false);
                                    }
                                    setMode('view');
                                }}
                            >
                                View
                            </button>
                            <button
                                className={`mode-tab ${mode === 'edit' ? 'active' : ''}`}
                                onClick={() => { setMode('edit'); setCastMode(false); }}
                            >
                                Edit
                            </button>
                        </div>
                    </div>
                    <button className="script-modal-close" onClick={handleClose} aria-label="Close"><X size={18} /></button>
                </div>

                {mode === 'view' && (
                    <div className="script-cast-toolbar">
                        <span className="cast-toolbar-label">View as:</span>
                        <div className="cast-mode-toggle">
                            <button
                                className={`cast-tab ${!castMode ? 'active' : ''}`}
                                onClick={() => setCastMode(false)}
                            >
                                Producer
                            </button>
                            <button
                                className={`cast-tab ${castMode ? 'active' : ''}`}
                                onClick={() => setCastMode(true)}
                            >
                                Cast
                            </button>
                        </div>
                    </div>
                )}

                {mode === 'edit' && (
                    <div className="script-editor-toolbar">
                        <button onClick={() => editor?.chain().focus().toggleBold().run()} className={editor?.isActive('bold') ? 'toolbar-btn active' : 'toolbar-btn'} title="Bold">B</button>
                        <button onClick={() => editor?.chain().focus().toggleItalic().run()} className={editor?.isActive('italic') ? 'toolbar-btn active' : 'toolbar-btn'} title="Italic"><em>I</em></button>
                        <button onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} className={editor?.isActive('heading', { level: 2 }) ? 'toolbar-btn active' : 'toolbar-btn'} title="Heading">H2</button>
                        <button onClick={() => editor?.chain().focus().toggleBulletList().run()} className={editor?.isActive('bulletList') ? 'toolbar-btn active' : 'toolbar-btn'} title="Bullet list">• List</button>
                        <button onClick={() => editor?.chain().focus().toggleOrderedList().run()} className={editor?.isActive('orderedList') ? 'toolbar-btn active' : 'toolbar-btn'} title="Numbered list">1. List</button>
                        <div className="toolbar-divider" />
                        <button onClick={() => editor?.chain().focus().undo().run()} className="toolbar-btn" title="Undo">↩</button>
                        <button onClick={() => editor?.chain().focus().redo().run()} className="toolbar-btn" title="Redo">↪</button>
                    </div>
                )}

                <div className={`script-modal-body ${mode === 'edit' ? 'edit-mode' : 'view-mode'}`}>
                    {mode === 'view' ? (
                        <div
                            className="script-editor-content"
                            dangerouslySetInnerHTML={{ __html: viewHtml }}
                        />
                    ) : (
                        <EditorContent editor={editor} className="script-editor-content" />
                    )}
                </div>

                {saveError && <div className="error-message" style={{ margin: '0 20px' }}>{saveError}</div>}

                {mode === 'view' && (
                    <div className="script-modal-footer">
                        <button
                            className="btn-secondary btn-sm"
                            onClick={handleExport}
                            disabled={isExporting}
                            style={{ marginRight: 'auto' }}
                        >
                            {isExporting ? 'Exporting…' : '↓ Export DOCX'}
                        </button>
                        <span className={`approval-badge approval-${approvalStatus}`}>
                            {approvalStatus === 'approved'
                                ? <><CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Approved</>
                                : <><Clock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Pending Review</>}
                        </span>
                        <button
                            className={`btn-approval ${approvalStatus === 'approved' ? 'btn-approval-revert' : 'btn-approval-approve'}`}
                            onClick={handleApprovalToggle}
                            disabled={isUpdatingApproval}
                        >
                            {isUpdatingApproval
                                ? 'Saving…'
                                : approvalStatus === 'approved'
                                    ? 'Revert to Pending'
                                    : 'Approve Script'}
                        </button>
                    </div>
                )}

                {mode === 'edit' && (
                    <div className="script-modal-footer">
                        <button className="btn-secondary" onClick={() => {
                            if (hasUnsavedChanges && !window.confirm('Discard unsaved changes?')) return;
                            setHasUnsavedChanges(false);
                            setMode('view');
                        }}>
                            Cancel
                        </button>
                        <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScriptModal;
